import React, { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { Button } from '@/components/ui/button';
import { LoadingState } from '@/components/ui/LoadingState';
import { ArrowRightIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';

interface VisitorDayData {
  day: string;
  visitor_count: number;
}

interface VisitorsPerDayProps {
  visitorsPerDay: VisitorDayData[];
  timeRange?: string;
  isLoading?: boolean;
}

const chartConfig = {
  visitor_count: {
    label: 'Visitors',
    color: 'hsl(var(--chart-2))',
  },
} satisfies ChartConfig;

const VisitorsPerDay: React.FC<VisitorsPerDayProps> = ({
  visitorsPerDay,
  timeRange = '7d',
  isLoading = false,
}) => {
  const navigate = useNavigate();

  const chartData = useMemo(
    () =>
      [...visitorsPerDay].sort(
        (a, b) => new Date(a.day).getTime() - new Date(b.day).getTime()
      ),
    [visitorsPerDay]
  );

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    if (timeRange === '24h') {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }
    if (timeRange === '7d') {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTooltipLabel = (value: string) => {
    const date = new Date(value);
    if (timeRange === '24h') {
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getTickCount = () => {
    switch (timeRange) {
      case '24h':
        return 6;
      case '7d':
        return 7;
      case '30d':
        return 10;
      case '90d':
        return 12;
      case '365d':
        return 12;
      default:
        return 7;
    }
  };

  const totalVisitors = chartData.reduce((sum, d) => sum + d.visitor_count, 0);

  return (
    <div className="px-2 pt-4 sm:px-6 sm:pt-6">
      {isLoading ? (
        <LoadingState label="Loading visitors..." className="min-h-[250px]" />
      ) : chartData.length === 0 ? (
        <div className="h-[250px] flex items-center justify-center text-muted-foreground">
          No data yet!
        </div>
      ) : (
        <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="fillVisitors" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-visitor_count)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="var(--color-visitor_count)" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="day"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={formatDate}
              tickCount={getTickCount()}
            />
            <YAxis tickLine={false} axisLine={false} allowDecimals={false} />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  className="w-[150px]"
                  labelFormatter={formatTooltipLabel}
                  indicator="dot"
                />
              }
            />
            <Area
              dataKey="visitor_count"
              type="monotone"
              fill="url(#fillVisitors)"
              stroke="var(--color-visitor_count)"
              name={chartConfig.visitor_count.label}
            />
            <ChartLegend content={<ChartLegendContent />} />
          </AreaChart>
        </ChartContainer>
      )}
      <Button
        variant="outline"
        className="mt-4 w-full"
        onClick={() => navigate('/liveview')}
      >
        View Visitors {totalVisitors > 0 ? `(${totalVisitors})` : ''}{' '}
        <ArrowRightIcon className="w-4 h-4 ml-2" />
      </Button>
    </div>
  );
};

export default VisitorsPerDay;
